import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  X,
  Minus,
  Zap,
  Clock,
  Camera,
  FileText,
  Globe,
  Video,
  Share2,
  Sparkles,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';

type CellValue = 'yes' | 'no' | 'partial' | string;

interface ComparisonRow {
  feature: string;
  description?: string;
  lb: CellValue;
  diy: CellValue;
  ai: CellValue;
  pro: CellValue;
}

// Honest, decision-driving comparison. Competitors are credited wherever they
// genuinely deliver (a photographer really does shoot great photos; ChatGPT
// really does write good copy) — fairness is what makes the wins that matter
// (cost, speed, all-in-one, scale) credible.
const comparisonData: ComparisonRow[] = [
  { feature: 'Time to a finished listing', description: 'From photos to a published, multi-channel listing', lb: '~10 min', diy: '3+ hrs', ai: '~1 hr', pro: '3–5 days' },
  { feature: 'Cost per listing', description: 'All-in cost to produce one property listing', lb: 'from $15', diy: 'Your time', ai: '$20+/mo', pro: '$300+' },
  { feature: 'Magazine-quality photos', description: 'Bright, sharp, professional-looking images', lb: 'yes', diy: 'no', ai: 'partial', pro: 'yes' },
  { feature: 'Virtual staging', description: 'Furnish empty rooms with realistic results', lb: 'yes', diy: 'no', ai: 'no', pro: 'partial' },
  { feature: 'Sky & lawn replacement', description: 'Fix overcast skies and dull lawns', lb: 'yes', diy: 'no', ai: 'no', pro: 'partial' },
  { feature: 'Persuasive, SEO-ready copy', description: 'Compelling descriptions that rank and sell', lb: 'yes', diy: 'no', ai: 'yes', pro: 'yes' },
  { feature: 'Publishes in 28+ languages', description: 'Reach international buyers natively', lb: 'yes', diy: 'no', ai: 'partial', pro: 'no' },
  { feature: 'Social media posts', description: 'Ready-to-post Facebook & Instagram content', lb: 'yes', diy: 'no', ai: 'partial', pro: 'no' },
  { feature: 'Property slideshow video', description: 'Auto-generated video with music & transitions', lb: 'yes', diy: 'no', ai: 'no', pro: 'partial' },
  { feature: 'Print-ready PDF flyer', description: 'Branded marketing flyer with QR code', lb: 'yes', diy: 'no', ai: 'partial', pro: 'yes' },
  { feature: 'Shareable microsite', description: 'A dedicated web page per listing', lb: 'yes', diy: 'no', ai: 'no', pro: 'partial' },
  { feature: 'Import from existing listing', description: 'Paste a URL to auto-import photos & details', lb: 'yes', diy: 'no', ai: 'no', pro: 'no' },
  { feature: 'Done-for-you, no skills needed', description: 'Professional results with zero learning curve', lb: 'yes', diy: 'no', ai: 'partial', pro: 'yes' },
  { feature: 'Scales across your portfolio', description: 'Fast and affordable across many listings', lb: 'yes', diy: 'no', ai: 'no', pro: 'no' },
];

const methods = [
  {
    key: 'lb' as const,
    name: 'ListBright',
    subtitle: 'AI-powered platform',
    highlighted: true,
  },
  {
    key: 'diy' as const,
    name: 'DIY (phone)',
    subtitle: 'Phone + free tools',
    highlighted: false,
  },
  {
    key: 'ai' as const,
    name: 'ChatGPT + Canva',
    subtitle: 'Generic AI + design',
    highlighted: false,
  },
  {
    key: 'pro' as const,
    name: 'Photographer + copywriter',
    subtitle: 'Hire professionals',
    highlighted: false,
  },
];

function CellIcon({ value, highlighted }: { value: CellValue; highlighted?: boolean }) {
  if (value === 'yes') {
    return (
      <div className="w-7 h-7 rounded-full bg-emerald-500/10 flex items-center justify-center">
        <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
      </div>
    );
  }
  if (value === 'no') {
    return (
      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
        <X className="w-4 h-4 text-muted-foreground/40" />
      </div>
    );
  }
  if (value === 'partial') {
    return (
      <div className="w-7 h-7 rounded-full bg-amber-500/10 flex items-center justify-center">
        <Minus className="w-4 h-4 text-amber-600 dark:text-amber-400" />
      </div>
    );
  }
  return (
    <span className={`text-sm font-semibold ${highlighted ? 'text-primary' : 'text-muted-foreground'}`}>
      {value}
    </span>
  );
}

export default function Compare() {
  const navigate = useNavigate();

  return (
    <Layout>
      <PageMeta
        title="ListBright vs ChatGPT & Manual Listings"
        description="Compare ListBright to ChatGPT, Canva, and manual methods for real estate listing creation. See why purpose-built AI wins on speed, quality, and features."
        breadcrumbs={[{ label: 'Compare', path: '/compare' }]}
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-20 lg:py-28">
        <div className="container max-w-5xl mx-auto px-4 text-center">
          <Badge variant="secondary" className="mb-4 px-4 py-2">
            <Sparkles className="w-4 h-4 mr-2" />
            Why ListBright?
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
            How Does ListBright{' '}
            <span className="text-gradient">Compare?</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            See how our purpose-built platform stacks up against ChatGPT, manual methods, 
            and generic design tools for creating property listings.
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="section-padding">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full min-w-[700px] border-collapse">
              {/* Header */}
              <thead>
                <tr>
                  <th className="text-left p-4 w-[280px]" />
                  {methods.map((m) => (
                    <th
                      key={m.key}
                      className={`p-4 text-center ${
                        m.highlighted
                          ? 'bg-primary/5 rounded-t-2xl border-x-2 border-t-2 border-primary/20'
                          : ''
                      }`}
                    >
                      <div className="space-y-1">
                        <p className={`font-bold text-base ${m.highlighted ? 'text-primary' : ''}`}>
                          {m.name}
                        </p>
                        <p className="text-xs text-muted-foreground font-normal">{m.subtitle}</p>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr
                    key={row.feature}
                    className={i % 2 === 0 ? 'bg-muted/30' : ''}
                  >
                    <td className="p-4">
                      <p className="font-medium text-sm">{row.feature}</p>
                      {row.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">{row.description}</p>
                      )}
                    </td>
                    {methods.map((m) => (
                      <td
                        key={m.key}
                        className={`p-4 text-center ${
                          m.highlighted ? 'bg-primary/5 border-x-2 border-primary/20' : ''
                        } ${i === comparisonData.length - 1 && m.highlighted ? 'border-b-2 rounded-b-2xl' : ''}`}
                      >
                        <div className="flex justify-center">
                          <CellIcon value={row[m.key]} highlighted={m.highlighted} />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-6 justify-center mt-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-green-500/15 flex items-center justify-center">
                <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
              </div>
              Fully supported
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-amber-500/15 flex items-center justify-center">
                <Minus className="w-3 h-3 text-amber-600 dark:text-amber-400" />
              </div>
              Partially supported
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                <X className="w-3 h-3 text-muted-foreground/40" />
              </div>
              Not available
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Breakdown Cards */}
      <section className="bg-card border-y border-border/40 section-padding">
        <div className="container max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-display font-bold text-center mb-4">
            The Real Difference
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Let's break down what it actually looks like to create a listing with each method.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* ListBright */}
            <div className="p-8 rounded-2xl border-2 border-primary/30 bg-primary/5 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">With ListBright</h3>
                  <p className="text-xs text-muted-foreground">About 10 minutes · from $15</p>
                </div>
              </div>
              <ol className="space-y-3 text-sm">
                {[
                  'Upload photos or paste a listing URL',
                  'AI enhances every photo automatically',
                  'AI writes descriptions in 28+ languages',
                  'Get a video, flyer & microsite instantly',
                  'Share everywhere with one click',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* DIY (phone) */}
            <div className="p-8 rounded-2xl border border-border/60 bg-muted/30 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">DIY on your phone</h3>
                  <p className="text-xs text-muted-foreground">~3 hours · just your time</p>
                </div>
              </div>
              <ol className="space-y-3 text-sm text-muted-foreground">
                {[
                  'You know your property best',
                  'Raw phone photos — no real editing',
                  'Write the whole description yourself',
                  'No video, flyer or microsite',
                  'Post manually to every channel',
                  'Hours per listing, hard to scale',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-muted-foreground/20 flex items-center justify-center text-xs font-medium shrink-0">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* ChatGPT + Canva */}
            <div className="p-8 rounded-2xl border border-border/60 bg-muted/30 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">ChatGPT + Canva</h3>
                  <p className="text-xs text-muted-foreground">~1 hour · $20+/mo</p>
                </div>
              </div>
              <ol className="space-y-3 text-sm text-muted-foreground">
                {[
                  'Genuinely good at descriptions',
                  'Canva templates look professional',
                  'Translation works reasonably well',
                  'No real photo enhancement or staging',
                  'No auto video or shareable microsite',
                  'Constant copy-paste between tools',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-muted-foreground/20 flex items-center justify-center text-xs font-medium shrink-0">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Photographer + copywriter */}
            <div className="p-8 rounded-2xl border border-border/60 bg-muted/30 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <Camera className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Photographer + copywriter</h3>
                  <p className="text-xs text-muted-foreground">3–5 days · $300+ per listing</p>
                </div>
              </div>
              <ol className="space-y-3 text-sm text-muted-foreground">
                {[
                  'Best-in-class photos and copy',
                  'True professional, human quality',
                  'Expensive — $300+ every listing',
                  'Days of turnaround per property',
                  'Rarely includes video or microsite',
                  'Doesn\'t scale for high volume',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-muted-foreground/20 flex items-center justify-center text-xs font-medium shrink-0">
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom line summary */}
      <section className="section-padding">
        <div className="container max-w-3xl mx-auto px-4 text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm font-medium">
            <AlertTriangle className="w-4 h-4" />
            The bottom line
          </div>
          <h2 className="text-3xl sm:text-4xl font-display font-bold">
            Other tools give you pieces.{' '}
            <span className="text-gradient">ListBright gives you the whole listing.</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            ChatGPT writes great copy but can't touch your photos. A photographer shoots beautiful photos but charges
            hundreds and takes days. Doing it yourself eats your whole afternoon. ListBright is the only one that does
            it all — photos, descriptions, video, flyer and microsite — in one place, in about 10 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button variant="hero" size="lg" onClick={() => navigate('/?create=true')}>
              <Zap className="w-5 h-5 mr-2" />
              Try ListBright Free
            </Button>
            <Button variant="outline" size="lg" onClick={() => navigate('/pricing')}>
              View Pricing
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
